import {
  Card,
  Skeleton,
  SkeletonBlock,
  SkeletonLine,
} from '@one-thd/sui-atomic-components';
import { AxiosError } from 'axios';
import { ErrorBoundary } from '@thd-olt-component-react/error-boundary';
import React, { useEffect, useState, JSX } from 'react';
import { usePurchaseHistoryContext } from '../../../hooks/useContexts';
import { IRecord } from '../../../models/purchase-history';
import NoOrdersView from '../NoOrdersView';
import { PHPagination } from '../PHPagination';
import { PHCard } from './PHCard';
import { NoOrdersFound } from '../order-search/NoOrdersFound';
import { FeatureSwitches } from '../../../constants/feature-switches';

export interface IPHCardView {
  customerRole: string;
  customerType: string;
}

const cardSkeleton = (key: string) => (
  <div key={key} className="sui-mb-6">
    <Card grow>
      <div className="sui-flex sui-min-w-full sui-gap-10">
        <div className="sui-w-5/12 sui-flex sui-flex-col sui-gap-4">
          <SkeletonLine variant="heading" fullWidth />
          <SkeletonLine numberOfLines={3} />
          <Skeleton disablePadding orientation="horizontal">
            {[...Array(4)].map((__f, jkey) => (
              <div key={jkey}>
                <SkeletonBlock height={12} width={12} />
              </div>
            ))}
          </Skeleton>
        </div>
        <div className="sui-w-7/12 sui-flex sui-flex-col sui-gap-4">
          <div className="sui-ml-auto sui-mb-auto">
            <SkeletonBlock height={8} width={8} />
          </div>
          <SkeletonLine numberOfLines={3} />
          <SkeletonLine variant="heading" fullWidth />
        </div>
      </div>
    </Card>
  </div>
);

const PHCardViewSkeleton = (): JSX.Element => {
  return (
    <>
      {[...Array(3)].map((_e, index: number) =>
        cardSkeleton(`ph-card-skeleton-${index}`),
      )}
    </>
  );
};

export const PHCardView = ({ customerRole, customerType }: IPHCardView) => {
  const {
    orderHistoryData,
    orderHistoryLoaded,
    orderHistoryError,
    setAlertState,
    setOpenAddReceiptDrawer,
    dateSelectorValue,
    pHRequest,
  } = usePurchaseHistoryContext();
  const [renderBuffer, setRenderBuffer] = useState<boolean>(false);
  const records: IRecord[] | undefined =
    orderHistoryData?.orders ?? orderHistoryData?.lineItems;

  const totalNumberOfOrders =
    orderHistoryData?.orderCount || orderHistoryData?.itemsCount || 0;

  setTimeout(() => {
    setRenderBuffer(true);
  }, 500);

  useEffect(() => {
    if ((orderHistoryError as AxiosError)?.response?.status === 500) {
      setAlertState({
        alertTitle: 'Purchase History Unavailable',
        alertMessage:
          "We're sorry, there is a technical error. Please try again later.",
        status: 'error',
      });
    }
  }, [orderHistoryError]);

  if (
    orderHistoryLoaded &&
    (totalNumberOfOrders === 0 ||
      orderHistoryData?.orders?.length === 0 ||
      (orderHistoryError as AxiosError)?.response?.status !== undefined)
  ) {
    if (FeatureSwitches.enableCPSOrderLevelSearch && pHRequest?.searchValue) {
      return (
        <ErrorBoundary name="no-orders-found">
          <NoOrdersFound searchText={pHRequest.searchValue} />
        </ErrorBoundary>
      );
    }
    return (
      <ErrorBoundary name="no-orders">
        <NoOrdersView
          customerType={customerType}
          setOpenAddReceiptModal={setOpenAddReceiptDrawer}
          activeFilter={dateSelectorValue === 'last6Months'}
        />
      </ErrorBoundary>
    );
  }

  if (orderHistoryLoaded) {
    return (
      <div>
        {records?.map((record: IRecord, index) => (
          <PHCard
            key={index}
            customerRole={customerRole}
            customerType={customerType}
            record={record}
            index={index}
          />
        ))}
        <ErrorBoundary name="pagination">
          <PHPagination />
        </ErrorBoundary>
      </div>
    );
  }

  return renderBuffer ? <PHCardViewSkeleton /> : <></>;
};
